/** @jsxImportSource @emotion/react */

import { Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { createSearchParams, useLocation, useSearchParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import TabsNavigation from 'components/TabsNavigation/TabsNavigation'
import { TabsItem } from 'types/tabsItem'
import { cmsRoutes, RouteValue, useLink } from 'routes/cmsRoutes'
import { CanadianDatTipsTypeEnum, PlatformName } from '__generatedTypes__/globalTypes'
import { FIND_ALL_CANADIAN_DAT_TIPS } from 'queries/canadianDatTips'
import { findAllCanadianDatTips, findAllCanadianDatTipsVariables } from '__generatedTypes__/findAllCanadianDatTips'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { PageLoader } from 'components/PageLoader'
import * as classes from './styles'
import { EditForm } from './EditForm'

const TABS: Array<{ id: CanadianDatTipsTypeEnum; title: string }> = [
  {
    id: CanadianDatTipsTypeEnum.PreparingForDAT,
    title: 'Preparing for the DAT',
  },
  {
    id: CanadianDatTipsTypeEnum.TakingNotes,
    title: 'Taking Notes',
  },
  {
    id: CanadianDatTipsTypeEnum.SoapCarvingTips,
    title: 'Soap Carving Tips',
  },
  {
    id: CanadianDatTipsTypeEnum.OnDayOfDAT,
    title: 'On the day of the DAT',
  },
]

export const QUERY_PARAM = 'tab'

export const CanadianDatTips = () => {
  const { selectedPlatform } = usePlatformsAndSubjects({})
  const location = useLocation()
  const { navigate } = useLink()
  const [searchParams] = useSearchParams()

  const [activeTab, setActiveTab] = useState<TabsItem>(() => {
    const tabId = searchParams.get(QUERY_PARAM)

    return TABS.find(({ id }) => id === tabId) || TABS[0]
  })

  const { data, loading } = useQuery<findAllCanadianDatTips, findAllCanadianDatTipsVariables>(
    FIND_ALL_CANADIAN_DAT_TIPS,
    {
      variables: {
        query: {},
      },
      fetchPolicy: 'network-only',
    }
  )
  const canadianDatTips = data?.findAllCanadianDatTip.items
  const selectedCanadianDatTip = canadianDatTips?.find(({ title }) => title === activeTab.id)

  useEffect(() => {
    if (selectedPlatform && selectedPlatform.title !== PlatformName.DATCrusher) {
      navigate(`/${String(selectedPlatform.slug)}/${cmsRoutes.NOT_FOUND}`)
    }
  }, [selectedPlatform?.title])

  const handleChangeTab = (activeTab: TabsItem) => {
    navigate(location.pathname as RouteValue, undefined, {
      searchParams: createSearchParams({ [QUERY_PARAM]: activeTab.id }),
    })
    setActiveTab(activeTab)
  }

  if (!selectedPlatform || loading || !selectedCanadianDatTip) {
    return <PageLoader />
  }

  return (
    <section css={classes.container}>
      <Typography css={classes.header} variant="title3">
        Canadian DAT Tips
      </Typography>

      <TabsNavigation css={classes.tabs} tabs={TABS} value={activeTab} onChange={handleChangeTab} />

      <EditForm canadianDatTip={selectedCanadianDatTip} />
    </section>
  )
}
